import React, { ReactNode } from "react";
import { Button } from "antd";

import { ChevronRight, Clock } from "./icons";
import styles from "./fundForm.module.scss";

export type TFundFormSection = {
  id: string;
  label: string;
};

interface IFundFormShell {
  crumb: string;
  title: string;
  /** The draft / active chip beside the actions. */
  status?: { label: string; tone?: "draft" | "live" };
  sections: TFundFormSection[];
  activeSection: string;
  onSelectSection: (id: string) => void;
  railNote?: ReactNode;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
  submitting?: boolean;
  children: ReactNode;
}

/**
 * The page around the form: a sticky action bar, a section rail, one column.
 *
 * The form used to run as a single column for roughly three screens with its
 * only actions pinned to the very top, so the further down you were the less
 * you could tell where you were or how to save. The bar and the rail both
 * stay put.
 */
export default function FundFormShell({
  crumb,
  title,
  status,
  sections,
  activeSection,
  onSelectSection,
  railNote,
  onCancel,
  onSubmit,
  submitLabel,
  submitting = false,
  children,
}: IFundFormShell) {
  return (
    <div className={styles.page}>
      <div className={styles.actionBar}>
        <div>
          <div className={styles.crumbs}>
            <span>Investments</span>
            <ChevronRight color="#8b8699" />
            <span>{crumb}</span>
          </div>
          <h1 className={styles.pageTitle}>{title}</h1>
        </div>

        <div className={styles.actions}>
          {status ? (
            <span className={styles.statusPill}>
              <Clock color="#713fff" />
              {status.label}
            </span>
          ) : null}

          <Button size="large" onClick={onCancel} style={{ height: 48, borderRadius: 8 }}>
            Cancel
          </Button>

          <Button
            size="large"
            type="primary"
            loading={submitting}
            onClick={onSubmit}
            style={{ height: 48, borderRadius: 8 }}
          >
            {submitLabel}
          </Button>
        </div>
      </div>

      <div className={styles.body}>
        <nav className={styles.rail}>
          <div className={styles.railHeading}>Sections</div>

          {sections.map((section, index) => {
            const isActive = section.id === activeSection;

            return (
              <button
                key={section.id}
                type="button"
                className={`${styles.railItem} ${
                  isActive ? styles.railItemActive : ""
                }`}
                onClick={() => onSelectSection(section.id)}
              >
                <span
                  className={`${styles.railStep} ${
                    isActive ? styles.railStepActive : ""
                  }`}
                >
                  {index + 1}
                </span>
                {section.label}
              </button>
            );
          })}

          {railNote ? (
            <>
              <div className={styles.railDivider} />
              <div className={styles.railNote}>{railNote}</div>
            </>
          ) : null}
        </nav>

        <div className={styles.column}>{children}</div>
      </div>
    </div>
  );
}
